import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Building2,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useHeaderActions } from "../context/HeaderActionsContext";
import {
  formatAdminDisplayName,
  isAdminUserMissing,
  shouldShowAdminUsername,
} from "./organizations-admin/formatters";

export type Organization = {
  id: string;
  name: string;
  created_at: string | null;
  updated_at: string | null;
  org_admin_user_id: number | null;
  teachers_can_use_studio: boolean;
  contact_email: string | null;
  contact_phone: string | null;
  /**
   * The resolved user row. Null means org_admin_user_id matched nothing —
   * distinct from a user who exists with a null name or username.
   */
  admin_user_id: number | null;
  admin_name: string | null;
  admin_username: string | null;
  admin_email: string | null;
  member_count: number;
};

type SortField = "name" | "created_at" | "updated_at" | "member_count";
type SortOrder = "asc" | "desc";

const SortIcon = ({
  field,
  currentField,
  sortOrder,
}: {
  field: SortField;
  currentField: SortField;
  sortOrder: SortOrder;
}) => {
  if (currentField !== field) return null;
  return sortOrder === "asc" ? (
    <ArrowUp className="ml-1 h-3 w-3 inline" />
  ) : (
    <ArrowDown className="ml-1 h-3 w-3 inline" />
  );
};

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const OrganizationsAdmin = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [savingOrgId, setSavingOrgId] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const { setActions } = useHeaderActions();

  const loadOrganizations = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetch("/api/internal/admin/organizations/details", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`organizations ${res.status}`);
      setOrganizations((await res.json()) as Organization[]);
    } catch (err) {
      toast.error("Failed to load organizations");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    setActions(
      <Button
        variant="outline"
        size="icon"
        onClick={loadOrganizations}
        disabled={isLoading}
        className="h-8 w-8"
      >
        <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
      </Button>,
    );
    return () => setActions(null);
  }, [setActions, isLoading, loadOrganizations]);

  useEffect(() => {
    const handleSlash = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      )
        return;
      if (event.key === "/") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleSlash);
    return () => window.removeEventListener("keydown", handleSlash);
  }, []);

  const handleStudioFlagToggle = async (
    org: Organization,
    enabled: boolean,
  ) => {
    try {
      setSavingOrgId(org.id);
      const res = await fetch(
        `/api/internal/admin/organizations/${encodeURIComponent(org.id)}/studio-flag`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ teachers_can_use_studio: enabled }),
        },
      );
      if (!res.ok) throw new Error(`studio-flag ${res.status}`);
      const updated = (await res.json()) as {
        teachers_can_use_studio: boolean;
        updated_at: string | null;
      };
      // Apply the server's values rather than the optimistic ones so the
      // Updated column reflects the bump the write just made.
      setOrganizations((previous) =>
        previous.map((row) =>
          row.id === org.id
            ? {
                ...row,
                teachers_can_use_studio: updated.teachers_can_use_studio,
                updated_at: updated.updated_at,
              }
            : row,
        ),
      );
      toast.success(
        `Teachers ${enabled ? "can now" : "can no longer"} use Studio in ${org.name}`,
      );
    } catch (err) {
      toast.error(`Failed to update Studio access for ${org.name}`);
      console.error(err);
    } finally {
      setSavingOrgId(null);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const visibleOrganizations = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();
    const filtered = search
      ? organizations.filter((org) =>
          [
            org.name,
            org.id,
            org.contact_email,
            org.admin_name,
            org.admin_username,
            org.admin_email,
            org.org_admin_user_id?.toString(),
          ].some((field) => field?.toLowerCase().includes(search)),
        )
      : organizations;

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      if (sortField === "name") {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === "member_count") {
        comparison = a.member_count - b.member_count;
      } else {
        // created_at / updated_at — nulls sort last in ascending order
        const aTime = a[sortField] ? new Date(a[sortField]).getTime() : null;
        const bTime = b[sortField] ? new Date(b[sortField]).getTime() : null;
        if (aTime === null || bTime === null) {
          comparison = aTime === null ? (bTime === null ? 0 : 1) : -1;
        } else {
          comparison = aTime - bTime;
        }
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [organizations, searchQuery, sortField, sortOrder]);

  const studioEnabledCount = organizations.filter(
    (org) => org.teachers_can_use_studio,
  ).length;

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden">
      <Card className="rounded-xl border-border/60 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, org admin, contact email, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-14"
            />
            <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              /
            </kbd>
          </div>
          <div className="text-sm text-muted-foreground">
            {visibleOrganizations.length}
            {visibleOrganizations.length !== organizations.length
              ? ` of ${organizations.length}`
              : ""}{" "}
            organizations • {studioEnabledCount} with Studio enabled
          </div>
        </div>
      </Card>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-border/60 shadow-sm">
        <div className="relative min-h-0 flex-1 overflow-auto">
          <Table className="min-w-full">
            <TableHeader>
              <TableRow className="sticky top-0 z-10 border-b border-border/60 bg-card/95 backdrop-blur hover:bg-transparent">
                <TableHead
                  className="min-w-[200px] px-3 text-xs font-semibold uppercase tracking-[0.18em] cursor-pointer select-none hover:bg-muted/60"
                  onClick={() => handleSort("name")}
                >
                  <span className="flex items-center gap-1">
                    Name{" "}
                    <SortIcon
                      field="name"
                      currentField={sortField}
                      sortOrder={sortOrder}
                    />
                  </span>
                </TableHead>
                <TableHead
                  className="w-44 px-3 text-xs font-semibold uppercase tracking-[0.18em] cursor-pointer select-none hover:bg-muted/60"
                  onClick={() => handleSort("created_at")}
                >
                  <span className="flex items-center gap-1">
                    Created{" "}
                    <SortIcon
                      field="created_at"
                      currentField={sortField}
                      sortOrder={sortOrder}
                    />
                  </span>
                </TableHead>
                <TableHead
                  className="w-44 px-3 text-xs font-semibold uppercase tracking-[0.18em] cursor-pointer select-none hover:bg-muted/60"
                  onClick={() => handleSort("updated_at")}
                >
                  <span className="flex items-center gap-1">
                    Updated{" "}
                    <SortIcon
                      field="updated_at"
                      currentField={sortField}
                      sortOrder={sortOrder}
                    />
                  </span>
                </TableHead>
                <TableHead className="min-w-[180px] px-3 text-xs font-semibold uppercase tracking-[0.18em]">
                  Org Admin
                </TableHead>
                <TableHead className="w-40 px-3 text-center text-xs font-semibold uppercase tracking-[0.18em]">
                  Teachers Use Studio
                </TableHead>
                <TableHead className="min-w-[180px] px-3 text-xs font-semibold uppercase tracking-[0.18em]">
                  Contact Email
                </TableHead>
                <TableHead
                  className="w-24 px-3 text-right text-xs font-semibold uppercase tracking-[0.18em] cursor-pointer select-none hover:bg-muted/60"
                  onClick={() => handleSort("member_count")}
                >
                  <span className="flex items-center justify-end gap-1">
                    Members{" "}
                    <SortIcon
                      field="member_count"
                      currentField={sortField}
                      sortOrder={sortOrder}
                    />
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && organizations.length === 0 ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="px-3 py-2">
                      <Skeleton className="h-3.5 w-40" />
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <Skeleton className="h-3.5 w-28" />
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <Skeleton className="h-3.5 w-28" />
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <Skeleton className="h-3.5 w-32" />
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <Skeleton className="mx-auto h-5 w-16 rounded-full" />
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <Skeleton className="h-3.5 w-36" />
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <Skeleton className="ml-auto h-3.5 w-8" />
                    </TableCell>
                  </TableRow>
                ))
              ) : visibleOrganizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-40 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <div className="rounded-full bg-muted/40 p-3">
                        <Building2 className="h-10 w-10 opacity-60" />
                      </div>
                      <p>No organizations found</p>
                      {searchQuery && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSearchQuery("")}
                        >
                          Clear search
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                visibleOrganizations.map((org) => (
                  <TableRow
                    key={org.id}
                    className="border-b last:border-0 hover:bg-muted/40"
                  >
                    <TableCell className="px-3 py-2">
                      <div className="flex flex-col">
                        <span className="truncate text-sm font-medium">
                          {org.name}
                        </span>
                        <span className="font-mono text-[11px] text-muted-foreground">
                          {org.id}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-3 py-2 text-xs whitespace-nowrap text-muted-foreground">
                      {formatDate(org.created_at)}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-xs whitespace-nowrap text-muted-foreground">
                      {formatDate(org.updated_at)}
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      {org.org_admin_user_id == null ? (
                        <span className="text-sm text-muted-foreground">
                          Unassigned
                        </span>
                      ) : (
                        <div className="flex flex-col">
                          <span className="truncate text-sm">
                            {formatAdminDisplayName(org)}
                            {shouldShowAdminUsername(org) ? (
                              <span className="text-muted-foreground">
                                {" "}
                                @{org.admin_username}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            ID: {org.org_admin_user_id}
                            {isAdminUserMissing(org)
                              ? " (no matching user)"
                              : ""}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <div
                        className="flex items-center justify-center"
                        title="teachers_can_use_studio — gates ET Studio access for this org's teachers"
                      >
                        {savingOrgId === org.id ? (
                          <Loader2 className="size-4 animate-spin text-muted-foreground" />
                        ) : (
                          <Switch
                            checked={org.teachers_can_use_studio}
                            onCheckedChange={(checked) =>
                              handleStudioFlagToggle(org, checked)
                            }
                            disabled={savingOrgId !== null}
                            aria-label={`Teachers can use Studio for ${org.name}`}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <span className="block truncate text-sm text-muted-foreground">
                        {org.contact_email || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="px-3 py-2 text-right text-sm tabular-nums text-muted-foreground">
                      {org.member_count}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};
